import React, { useEffect, useRef, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import imageCompression from "browser-image-compression";
import axiosInstance from "../services/axiosInterceptor";
import { updateStoredUser } from "../services/authorize";
import userAvatar from "../assets/img/img1.jpg";

const EMPTY = { t_idnumber: "", t_name: "", t_arabicname: "", t_photo: "", t_gen: "m", t_phone: "", t_birthdate: "" };

const UPLOAD_BASE = (process.env.REACT_APP_API || "http://localhost:3002/api")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");
const PHOTO_COMPRESS_OPTS = { maxSizeMB: 0.8, maxWidthOrHeight: 1280, useWebWorker: true, fileType: "image/jpeg" };

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("blob:")) return path;
  return `${UPLOAD_BASE}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function ProfileEdit() {
  const navigate = useNavigate();
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  useEffect(() => {}, [skin]);

  useEffect(() => {
    axiosInstance.get("/api/profile")
      .then((res) => {
        setForm({ ...EMPTY, ...res.data });
        setPhotoPreview(resolveUploadUrl(res.data.t_photo));
      })
      .catch((err) =>
        Swal.fire("ผิดพลาด", err.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ", "error")
      )
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      Swal.fire("ไฟล์ไม่ถูกต้อง", "กรุณาเลือกไฟล์รูปภาพ", "warning");
      return;
    }

    setUploadingPhoto(true);
    try {
      let toUpload = file;
      try {
        const compressed = await imageCompression(file, PHOTO_COMPRESS_OPTS);
        toUpload = new File([compressed], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
      } catch (err) {
        console.warn("Profile photo compression failed, using original file", err);
      }

      const fd = new FormData();
      fd.append("profilePhoto", toUpload);
      const { data } = await axiosInstance.post("/api/profile/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const photoPath = data.photoPath || "";
      setForm((prev) => ({ ...prev, t_photo: photoPath }));
      setPhotoPreview(resolveUploadUrl(photoPath));
      updateStoredUser({ t_photo: photoPath });
      Swal.fire({ icon: "success", title: "อัปโหลดรูปสำเร็จ", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire("อัปโหลดรูปไม่สำเร็จ", err.response?.data?.message || err.message, "error");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.t_name.trim()) {
      Swal.fire("กรุณากรอกชื่อ-สกุล", "", "warning");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosInstance.put("/api/profile", {
        t_name: form.t_name,
        t_arabicname: form.t_arabicname,
        t_gen: form.t_gen,
        t_phone: form.t_phone,
        t_birthdate: form.t_birthdate,
      });
      // keep header/name cache in sync
      updateStoredUser({ t_name: data.t_name });
      await Swal.fire({ icon: "success", title: "บันทึกข้อมูลเรียบร้อย", timer: 1200, showConfirmButton: false });
      navigate("/account/profile");
    } catch (err) {
      Swal.fire("บันทึกไม่สำเร็จ", err.response?.data?.message || err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/account/profile">โปรไฟล์</Link></li>
              <li className="breadcrumb-item active" aria-current="page">แก้ไขโปรไฟล์</li>
            </ol>
            <h4 className="main-title mb-0">แก้ไขโปรไฟล์</h4>
          </div>
        </div>

        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="card-one">
              <Card.Header><Card.Title as="h6">ข้อมูลส่วนตัว</Card.Title></Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <div className="d-flex flex-column align-items-center text-center mb-4">
                      <div className="avatar avatar-xl mb-3">
                        <img src={photoPreview || userAvatar} alt="avatar" />
                      </div>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={handlePhotoChange}
                      />
                      <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        disabled={uploadingPhoto}
                        onClick={() => photoInputRef.current?.click()}
                      >
                        {uploadingPhoto ? (
                          <><Spinner size="sm" className="me-2" />กำลังอัปโหลด...</>
                        ) : (
                          <><i className="ri-camera-line me-1"></i>{photoPreview ? "เปลี่ยนรูปโปรไฟล์" : "เพิ่มรูปโปรไฟล์"}</>
                        )}
                      </Button>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label>รหัสประจำตัว / ชื่อผู้ใช้</Form.Label>
                      <Form.Control type="text" value={form.t_idnumber} disabled readOnly />
                      <Form.Text className="text-muted">ไม่สามารถแก้ไขได้ หากต้องการเปลี่ยน โปรดติดต่อผู้ดูแลระบบ</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>ชื่อ - สกุล <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="t_name"
                        value={form.t_name}
                        onChange={handleChange}
                        required
                        placeholder="เช่น นายสมชาย ใจดี"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>ชื่อภาษาอาหรับ / ยาวี</Form.Label>
                      <Form.Control
                        type="text"
                        name="t_arabicname"
                        value={form.t_arabicname || ""}
                        onChange={handleChange}
                        dir="rtl"
                      />
                    </Form.Group>

                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>เพศ</Form.Label>
                          <Form.Select name="t_gen" value={form.t_gen || "m"} onChange={handleChange}>
                            <option value="m">ชาย</option>
                            <option value="f">หญิง</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>เบอร์โทรศัพท์</Form.Label>
                          <Form.Control
                            type="tel"
                            name="t_phone"
                            value={form.t_phone || ""}
                            onChange={handleChange}
                            placeholder="08x-xxx-xxxx"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-4">
                      <Form.Label>วันเกิด</Form.Label>
                      <Form.Control
                        type="date"
                        name="t_birthdate"
                        value={form.t_birthdate || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-2" />กำลังบันทึก...</> : <><i className="ri-save-line me-1"></i>บันทึก</>}
                      </Button>
                      <Button as={Link} to="/account/profile" variant="outline-secondary" disabled={saving}>
                        ยกเลิก
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
